// The Item Catalog list's *filter* vocabulary: the four radio groups, the
// show-deleted toggle, and the manufacturer and EI package id lists — what the URL may say, and
// what the page's controls offer.
//
// Beside `catalog-list-url-state.ts` rather than inside it: that module is the
// list's own shape (sort, view, page arithmetic) and this one is what narrows
// the result set. Both are `lib/` for the same reason — no React, no router, no
// wire faces (`client/docs/architecture.md`, "The dependency rule").
//
// Everything here is total. The URL is user-editable, so "invalid" is an
// ordinary case: the search schema (`@/api/contracts/catalog`) drops what it
// cannot read and these helpers ignore the rest, exactly as the list state's
// clamps do.

import { DEFAULT_CATALOG_PAGE } from "./catalog-list-url-state";

/**
 * A radio filter's three positions, as the URL spells them.
 *
 * The donor's spelling verbatim (`?filterEI=all|true|false`), and strings
 * rather than a `boolean | null` because that is what a URL carries and what a
 * radio group's `value` is. `parseTristateFilter` is the one place it becomes a
 * filter.
 */
export const TRISTATE_FILTER_VALUES = ["all", "true", "false"] as const;

export type TristateFilter = (typeof TRISTATE_FILTER_VALUES)[number];

export const DEFAULT_TRISTATE_FILTER = "all" satisfies TristateFilter;

export interface TristateFilterOption {
  value: TristateFilter;
  label: string;
}

/**
 * What each radio group reads to a filter.
 *
 * `undefined`, not `null`: absent is how the request face spells "no clause"
 * (`ListCatalogItemsInput`), so the page can spread the result straight into
 * the input and let the encoder drop it.
 */
export function parseTristateFilter(value: TristateFilter | undefined): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

export const CATALOG_EI_FILTER_OPTIONS: readonly TristateFilterOption[] = [
  { value: "all", label: "All items" },
  { value: "true", label: "EI items only" },
  { value: "false", label: "Non-EI items only" },
];

export const CATALOG_CORE_FILTER_OPTIONS: readonly TristateFilterOption[] = [
  { value: "all", label: "All items" },
  { value: "true", label: "Core items only" },
  { value: "false", label: "Non-core items only" },
];

export const CATALOG_APPROVED_FILTER_OPTIONS: readonly TristateFilterOption[] = [
  { value: "all", label: "All items" },
  { value: "true", label: "Approved items only" },
  { value: "false", label: "Not approved items only" },
];

/**
 * The recommendation group, whose two ends read backwards from the others.
 *
 * It filters `isNotRecommended`, so **"Recommended Only" is the `false`
 * position** — the donor spells this out in a comment above the same list, and
 * reversing it inverts the filter with no visible symptom but wrong rows. The
 * labels' capitalisation is the donor's too, and deliberately unlike the three
 * groups above it.
 */
export const CATALOG_RECOMMENDATION_FILTER_OPTIONS: readonly TristateFilterOption[] = [
  { value: "all", label: "All Items" },
  { value: "false", label: "Recommended Only" },
  { value: "true", label: "Not Recommended Only" },
];

/**
 * The ids in an id-list param — `?mfrs=` or `?tiers=` — in the order the URL
 * lists them.
 *
 * Space-separated, which is the donor's spelling for both
 * (`tier-filter-utils.ts` for the tiers). Selection order is the page's
 * business — the donor keeps manufacturers sorted by name and tiers in click
 * order — so this preserves whatever order it is given rather than imposing
 * one.
 *
 * Anything that is not a positive integer is dropped, and duplicates collapse
 * to their first mention: a hand-edited `?mfrs=4 x 4` filters by manufacturer 4
 * rather than erroring, and a repeated id would otherwise draw two chips for
 * one manufacturer.
 */
export function parseFilterIds(param: string | number | undefined): number[] {
  if (param === undefined || param === "") return [];
  // A number when the URL named exactly one id: the router JSON-parses each
  // param, so `?mfrs=7` never reaches here as a string.
  const ids = String(param)
    .split(" ")
    .filter(Boolean)
    .map((part) => Number(part))
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)];
}

/**
 * An id-list param's value, or nothing at all when nothing is selected.
 *
 * A lone id is written as a **number** rather than as a one-word string, so the
 * router leaves it bare: it re-serializes any string that JSON can parse, which
 * would turn `?mfrs=7` into `?mfrs=%227%22`. Two or more ids join with a space,
 * which JSON cannot parse, so that spelling survives untouched — and both are
 * what the donor's URLs look like.
 */
export function formatFilterIds(ids: readonly number[]): string | number | undefined {
  if (ids.length === 0) return undefined;
  return ids.length === 1 ? ids[0] : ids.join(" ");
}

/**
 * Everything the sidebar narrows the list by, resolved.
 *
 * The page reads it out of the URL and hands it down; the sidebar reports
 * changes back as a partial. `categorySlug: null` and `manufacturerIds: []` are
 * "no filter" rather than absent, because a control has to have *some* value to
 * render — the URL is where absence is spelled.
 */
export interface CatalogListFilters {
  categorySlug: string | null;
  manufacturerIds: number[];
  /** The EI Package filter's tier ids — ids, not the tier's `value`. */
  tierIds: number[];
  filterEI: TristateFilter;
  filterCore: TristateFilter;
  filterApproved: TristateFilter;
  filterRecommendationStatus: TristateFilter;
  showDeleted: boolean;
}

export const DEFAULT_CATALOG_FILTERS: CatalogListFilters = {
  categorySlug: null,
  manufacturerIds: [],
  tierIds: [],
  filterEI: DEFAULT_TRISTATE_FILTER,
  filterCore: DEFAULT_TRISTATE_FILTER,
  filterApproved: DEFAULT_TRISTATE_FILTER,
  filterRecommendationStatus: DEFAULT_TRISTATE_FILTER,
  showDeleted: false,
};

/**
 * The filter clauses the list request carries, from the resolved filters.
 *
 * Every "no filter" position becomes an **absent** key rather than a null: the
 * wire encoder sends exactly what it is handed, so a call that names no filters
 * says nothing about filters — and the empty manufacturer list in particular
 * must not travel, because Python reads a present-but-empty list as "match
 * none" (`@/api/contracts/catalog`).
 */
export function catalogFilterClauses(filters: CatalogListFilters): {
  categorySlug?: string;
  manufacturerIds?: number[];
  tierIds?: number[];
  hasEiTiers?: boolean;
  isCore?: boolean;
  isApproved?: boolean;
  isNotRecommended?: boolean;
  includeDeleted?: boolean;
} {
  const hasEiTiers = parseTristateFilter(filters.filterEI);
  const isCore = parseTristateFilter(filters.filterCore);
  const isApproved = parseTristateFilter(filters.filterApproved);
  const isNotRecommended = parseTristateFilter(filters.filterRecommendationStatus);

  return {
    ...(filters.categorySlug === null ? {} : { categorySlug: filters.categorySlug }),
    ...(filters.manufacturerIds.length === 0
      ? {}
      : { manufacturerIds: [...filters.manufacturerIds] }),
    ...(filters.tierIds.length === 0 ? {} : { tierIds: [...filters.tierIds] }),
    ...(hasEiTiers === undefined ? {} : { hasEiTiers }),
    ...(isCore === undefined ? {} : { isCore }),
    ...(isApproved === undefined ? {} : { isApproved }),
    ...(isNotRecommended === undefined ? {} : { isNotRecommended }),
    ...(filters.showDeleted ? { includeDeleted: true } : {}),
  };
}

/**
 * The eight params the filters live in, as the URL may spell them.
 *
 * Declared structurally here rather than imported from
 * `@/api/contracts/catalog`: that module imports *this* one for the tristate
 * vocabulary, and a schema is a validator anyway — what these two functions
 * need is the shape a validated search already has. `CatalogItemsSearch`
 * satisfies it, which `tsc` checks at every call site.
 */
export interface CatalogFilterParams {
  category?: string;
  mfrs?: string | number;
  tiers?: string | number;
  filterEI?: UrlTristate;
  filterCore?: UrlTristate;
  filterApproved?: UrlTristate;
  filterRecommendationStatus?: UrlTristate;
  showDeleted?: boolean | "true" | "false";
}

/**
 * A tristate as a URL may carry it.
 *
 * The router JSON-parses every param, so the two applied positions arrive as
 * **booleans** (`?filterEI=true` → `true`) and only `all` — which is not JSON —
 * stays a string. Reads accept both spellings; writes use the boolean, because
 * a string the router can parse gets re-quoted into `%22true%22`.
 */
export type UrlTristate = boolean | TristateFilter;

/**
 * A tristate as this app *writes* one — strictly narrower than `UrlTristate`.
 *
 * Reads accept `"true"` and `"false"` because a hand-edited or re-encoded link
 * can carry them. Writes must never emit them: the codec re-quotes any string
 * `JSON.parse` accepts, so `"true"` would land in the address bar as
 * `%22true%22` where the donor writes a bare `true`. The two legal spellings
 * are therefore a boolean, or the bare `all` that is not JSON and survives as
 * itself — and having the type say so is what keeps that rule from living only
 * in the comment on `tristateToUrl`.
 */
export type WrittenTristate = boolean | typeof DEFAULT_TRISTATE_FILTER;

/** A URL tristate, in the vocabulary the controls speak. */
function tristateFromUrl(value: UrlTristate | undefined, fallback: TristateFilter): TristateFilter {
  if (value === undefined) return fallback;
  if (typeof value === "boolean") return value ? "true" : "false";
  return value;
}

/**
 * The inverse: the two applied positions as booleans, the default as the bare
 * string `all`.
 *
 * All three positions are written — the donor's `handleTristateFilter` runs
 * `searchParams.set(filterName, value)` with the literal `all` / `true` /
 * `false` (`items-list-page.tsx:247-254`) — and the two spellings are what make
 * the bytes match: the router re-serializes any string JSON can parse, so
 * `"true"` would land as `%22true%22` while the boolean lands bare, and `all`
 * is not JSON so it survives as itself.
 */
function tristateToUrl(value: TristateFilter): WrittenTristate {
  if (value === DEFAULT_TRISTATE_FILTER) return value;
  return value === "true";
}

/**
 * What a filter *write* looks like, param by param — and the two classes the
 * donor's handlers split into, spelled in the types.
 *
 *   - **Explicit once touched** — the four tristates and `showDeleted`. Their
 *     handlers `.set()` unconditionally, default included, so these never carry
 *     `undefined`: a control that was used writes its value.
 *   - **Delete when empty** — `category`, `mfrs` and `tiers`. Their handlers
 *     `.delete()` when the selection empties, which is what `undefined` asks the
 *     router for.
 *
 * Spelled out rather than mapped off `CatalogFilterParams`, because both
 * mapping modifiers get it wrong — `Required` strips the `undefined` the second
 * class needs, and `Partial` would erase the distinction the two classes are.
 */
export interface CatalogFilterParamWrites {
  category: string | undefined;
  mfrs: string | number | undefined;
  tiers: string | number | undefined;
  filterEI: WrittenTristate;
  filterCore: WrittenTristate;
  filterApproved: WrittenTristate;
  filterRecommendationStatus: WrittenTristate;
  showDeleted: boolean;
}

/**
 * The URL's filter params, resolved into the values the controls render from.
 *
 * The inverse of `filterChangeToSearch` below, and its neighbour on purpose: the two
 * are one mapping read in two directions, and a filter added to one and
 * forgotten in the other is a control that reads back blank the moment the URL
 * round-trips.
 */
export function searchToFilters(search: CatalogFilterParams): CatalogListFilters {
  return {
    categorySlug: search.category ?? DEFAULT_CATALOG_FILTERS.categorySlug,
    manufacturerIds: parseFilterIds(search.mfrs),
    tierIds: parseFilterIds(search.tiers),
    filterEI: tristateFromUrl(search.filterEI, DEFAULT_CATALOG_FILTERS.filterEI),
    filterCore: tristateFromUrl(search.filterCore, DEFAULT_CATALOG_FILTERS.filterCore),
    filterApproved: tristateFromUrl(search.filterApproved, DEFAULT_CATALOG_FILTERS.filterApproved),
    filterRecommendationStatus: tristateFromUrl(
      search.filterRecommendationStatus,
      DEFAULT_CATALOG_FILTERS.filterRecommendationStatus,
    ),
    showDeleted: search.showDeleted === true || search.showDeleted === "true",
  };
}

/** The four radio groups, whose URL key is their filter key. */
const TRISTATE_FILTER_KEYS = [
  "filterEI",
  "filterCore",
  "filterApproved",
  "filterRecommendationStatus",
] as const;

/**
 * One sidebar change, as the URL params it writes — **only the keys it names**,
 * `page` included, **in the donor's insertion order**.
 *
 * Three things are load-bearing here, and each of them is a byte in the address
 * bar:
 *
 *   - **The unit of write is the control, not the filter set.** Each donor
 *     handler mutates the params already in the address and touches its own key
 *     alone: `handleTristateFilter` and `handleToggleFilter` `.set()` the one
 *     filter they were given (`items-list-page.tsx:238-254`), and the category
 *     and manufacturer writers do the same for theirs
 *     (`CategoryFilter.tsx:55-67`, `ManufacturersFilter.tsx:88-108`). So a
 *     filter the user has not touched keeps whatever the URL said — absent if
 *     never set, explicit if set earlier — rather than being rewritten by a
 *     neighbour's click.
 *   - **A key absent from the change writes nothing; a key present writes its
 *     class's value** (see `CatalogFilterParamWrites`). The two are different
 *     things and the distinction is the point: `{ categorySlug: null }` deletes
 *     the category, while a change that does not mention it leaves it alone.
 *     A key named as `undefined` counts as unmentioned — the ordinary reading of
 *     an optional field, and no caller spells a cleared filter that way (the
 *     cleared category is `null`, the cleared manufacturers `[]`).
 *   - **Every filter change writes page 1, and *where* it writes it differs by
 *     control.** The three `items-list-page` handlers lead with
 *     `searchParams.set('page', '1')` and set their filter second; the category
 *     and manufacturer writers set their own key first and `page` second. On a
 *     URL that does not already carry `page`, that ordering is the difference
 *     between `?page=1&category=faucets` and `?category=faucets&page=1` — two
 *     different strings for one click, which is exactly the defect #366 exists
 *     to close. So `category` and `mfrs` go in ahead of `page`, and the
 *     explicit class and `tiers` after it, which reproduces the donor for every
 *     change a control actually reports (each reports exactly one filter).
 */
export function filterChangeToSearch(
  change: Partial<CatalogListFilters>,
): Partial<CatalogFilterParamWrites> & { page: number } {
  const beforePage: Partial<CatalogFilterParamWrites> = {};
  const afterPage: Partial<CatalogFilterParamWrites> = {};

  // `null` is this filter's "cleared" and `undefined` its "not in this change",
  // so the two cannot collapse: only the first writes, and it writes a delete.
  if (change.categorySlug !== undefined) beforePage.category = change.categorySlug ?? undefined;
  if (change.manufacturerIds !== undefined) {
    beforePage.mfrs = formatFilterIds(change.manufacturerIds);
  }
  // After `page`: the EI Package select's `handleMultiSelectFilter` leads with
  // `set('page', '1')` and sets `tiers` second (`items-list-page.tsx:293-305`),
  // as the radios do. A chip removal sets `tiers` first, but `tiers` is already
  // in the address by then, so the merge keeps its place either way.
  if (change.tierIds !== undefined) afterPage.tiers = formatFilterIds(change.tierIds);
  for (const key of TRISTATE_FILTER_KEYS) {
    const value = change[key];
    if (value !== undefined) afterPage[key] = tristateToUrl(value);
  }
  if (change.showDeleted !== undefined) afterPage.showDeleted = change.showDeleted;

  // Spread order *is* the URL's param order — object key order is what the
  // search codec serializes in.
  return { ...beforePage, page: DEFAULT_CATALOG_PAGE, ...afterPage };
}
